import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { sportingService } from '../../services/sportingService';
import { LoadingSpinner } from '../../components/UI/LoadingSpinner';
import PlayerSelector from '../../components/Comparison/PlayerSelector';
import ComparisonRadarChart from '../../components/Comparison/ComparisonRadarChart';
import AttributeComparison from '../../components/Comparison/AttributeComparison';
import PlayerInfoCard from '../../components/Comparison/PlayerInfoCard';
import type { Player } from '../../types/player';
import toast from 'react-hot-toast';

const PlayerComparisonPage: React.FC = () => {
  const { t } = useTranslation();
  const [player1, setPlayer1] = useState<Player | null>(null);
  const [player2, setPlayer2] = useState<Player | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [availablePlayers, setAvailablePlayers] = useState<Player[]>([]);

  useEffect(() => {
    fetchAvailablePlayers();
  }, []);

  const fetchAvailablePlayers = async () => {
    try {
      setIsLoading(true);
      // Search for players with a broad query to get a good sample
      const response = await sportingService.searchPlayersAI('jugadores con estadísticas', 500, 0);
      
      // Get unique players
      const playerMap = new Map<string, Player>();
      response.jugadores.forEach((player: Player) => {
        const playerId = player.player_id || String(player.id_transfermarkt);
        if (playerId && !playerMap.has(playerId)) {
          playerMap.set(playerId, player);
        }
      });
      
      setAvailablePlayers(Array.from(playerMap.values()));
    } catch (error: any) {
      console.error('Error fetching players:', error);
      toast.error(t('errors.generic'));
    } finally {
      setIsLoading(false);
    }
  };

  const handlePlayer1Select = (player: Player | null) => {
    setPlayer1(player);
  };

  const handlePlayer2Select = (player: Player | null) => {
    setPlayer2(player);
  };

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-800 mb-2">
          {t('comparison.title')}
        </h1>
        <p className="text-gray-600">
          {t('comparison.subtitle')}
        </p>
      </div>

      {/* Player Selectors */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <PlayerSelector
          position={1}
          onSelectPlayer={handlePlayer1Select}
          selectedPlayer={player1}
          availablePlayers={availablePlayers}
        />
        <PlayerSelector
          position={2}
          onSelectPlayer={handlePlayer2Select}
          selectedPlayer={player2}
          availablePlayers={availablePlayers}
        />
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="flex justify-center items-center py-12">
          <LoadingSpinner />
        </div>
      )}

      {/* Comparison Results */}
      {player1 && player2 && !isLoading && (
        <>
          {/* Radar Chart */}
          <div className="bg-white rounded-lg shadow-md p-6 mb-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">
              {t('comparison.comparisonChart')}
            </h2>
            <ComparisonRadarChart player1={player1} player2={player2} />
          </div>

          {/* Player Info Cards */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
            <PlayerInfoCard 
              player={player1} 
              color="rgba(59, 130, 246, 0.8)"
            />
            <PlayerInfoCard 
              player={player2} 
              color="rgba(239, 68, 68, 0.8)"
            />
          </div>

          {/* Detailed Attribute Comparison */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">
              {t('comparison.detailedAnalysis')}
            </h2>
            <AttributeComparison player1={player1} player2={player2} />
          </div>
        </>
      )}

      {/* Empty State */}
      {(!player1 || !player2) && !isLoading && (
        <div className="text-center py-12 bg-gray-50 rounded-lg shadow-sm">
          <div className="max-w-md mx-auto">
            <svg
              className="w-24 h-24 mx-auto mb-4 text-gray-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
              />
            </svg>
            <p className="text-xl text-gray-600 mb-2">
              {!player1 && !player2 
                ? t('comparison.selectPrompt')
                : !player1 
                ? t('comparison.selectFirstPlayer')
                : t('comparison.selectSecondPlayer')
              }
            </p>
            <p className="text-gray-500">
              {t('comparison.subtitle')}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlayerComparisonPage;
